import { useMemo, useRef, useState } from 'react';
import type { SignArea } from '@sign/shared';
import SignaturePad from './SignaturePad';
import { composeDocOverlay } from '../utils/composeDocOverlay';

interface Props {
  docSrc: string;
  areas: SignArea[];
  submitting: boolean;
  /** 全部区域签完 → 合成好的成品图（已烤入签名）。 */
  onComplete: (composedDataUrl: string) => void;
}

/**
 * 文档叠加签署（sign v2）：全屏展示文档底图，在每个签名区域画可点框，
 * 点击 → 手写签名 → 签名落进该区域。全部签完后合成成品并回调提交。
 */
export default function DocSignView({ docSrc, areas, submitting, onComplete }: Props) {
  // areaId → 签名 dataURL
  const [sigs, setSigs] = useState<Record<string, string>>({});
  // 当前正在签的区域 id（打开签名板）
  const [active, setActive] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const signedCount = Object.keys(sigs).length;
  const allSigned = signedCount === areas.length && areas.length > 0;
  const activeArea = useMemo(() => areas.find((a) => a.id === active) || null, [areas, active]);

  const onPadSubmit = (dataUrl: string) => {
    if (!active) return;
    setSigs((prev) => ({ ...prev, [active]: dataUrl }));
    setActive(null);
  };

  const finish = async () => {
    setComposing(true);
    try {
      const composed = await composeDocOverlay({ docSrc, areas, signatures: sigs });
      onComplete(composed);
    } catch (err) {
      setComposing(false);
      alert(err instanceof Error ? err.message : '合成失败，请重试');
    }
  };

  return (
    <div>
      <h2 className="section__label">
        在文档上签字
        <span className="muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
          已签 {signedCount}/{areas.length} 处
        </span>
      </h2>
      <p className="muted" style={{ fontSize: 12.5, margin: '0 0 10px' }}>
        点击文档中高亮的签名区域即可签字。
      </p>

      {/* 文档 + 区域浮层 */}
      <div
        ref={wrapRef}
        style={{ position: 'relative', width: '100%', border: '1px solid #ececec', borderRadius: 8, overflow: 'hidden' }}
      >
        <img src={docSrc} alt="待签署文档" style={{ width: '100%', display: 'block' }} />
        {areas.map((a) => {
          const done = !!sigs[a.id];
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setActive(a.id)}
              style={{
                position: 'absolute',
                left: `${a.xPct * 100}%`,
                top: `${a.yPct * 100}%`,
                width: `${a.wPct * 100}%`,
                height: `${a.hPct * 100}%`,
                padding: 0,
                border: done ? '1.5px solid transparent' : '1.5px dashed #1a7f5a',
                borderRadius: 4,
                background: done ? 'transparent' : 'rgba(26,127,90,0.08)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
              aria-label={`签字：${a.label}`}
            >
              {done ? (
                <img
                  src={sigs[a.id]}
                  alt={`${a.label}签名`}
                  style={{ width: '84%', height: '84%', objectFit: 'contain' }}
                />
              ) : (
                <span style={{ fontSize: 11, color: '#1a7f5a', background: '#fff', padding: '1px 5px', borderRadius: 3 }}>
                  ✍ {a.label}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* 签名板：点了某区域才出现 */}
      {activeArea && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 6 }}>
            签字：{activeArea.label}
          </div>
          <SignaturePad onSubmit={onPadSubmit} submitting={false} disabled={false} />
          <button
            type="button"
            className="btn"
            style={{ marginTop: 8, width: '100%' }}
            onClick={() => setActive(null)}
          >
            取消
          </button>
        </div>
      )}

      {/* 全部签完 → 提交 */}
      {!activeArea && (
        <button
          type="button"
          className="btn btn--primary"
          style={{ marginTop: 14, width: '100%' }}
          disabled={!allSigned || submitting || composing}
          onClick={() => void finish()}
        >
          {composing || submitting ? '提交中…' : allSigned ? '确认并提交' : `还需签 ${areas.length - signedCount} 处`}
        </button>
      )}
    </div>
  );
}
